import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMagic, 
    faArrowLeft, 
    faArrowRight, 
    faSpinner, 
    faPencilAlt,
    faEye,
    faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { drawOnCanvas } from './canvasUtils';
import { doc, getDoc } from "firebase/firestore";

const Step4 = ({
    layoutSuggestions,
    handleTitleChange,
    handleDescriptionChange,
    handleGenerateLayouts,
    loadingAI,
    aiError,
    prevStep,
    nextStep,
    aiGeneratedLayouts,
    quiltGrid,
    gridSize,
    logoSrc,
    db
}) => {
    const [showBasicPreview, setShowBasicPreview] = useState(false);
    const canvasRef = useRef(null);
    const [canvasKey, setCanvasKey] = useState(Date.now());
    const [localSuggestions, setLocalSuggestions] = useState([]);
    
    // Add refs for tracking cursor positions
    const titleInputRefs = useRef([]);
    const descriptionInputRefs = useRef([]);
    // Add state to track cursor positions
    const [titleCursorPositions, setTitleCursorPositions] = useState({});
    const [descriptionCursorPositions, setDescriptionCursorPositions] = useState({});
    
    // Initialize refs array when suggestions change
    useEffect(() => {
        if (localSuggestions.length > 0) {
            titleInputRefs.current = Array(localSuggestions.length).fill().map((_, i) => 
                titleInputRefs.current[i] || React.createRef()
            );
            
            descriptionInputRefs.current = Array(localSuggestions.length).fill().map((_, i) => 
                descriptionInputRefs.current[i] || React.createRef()
            );
        }
    }, [localSuggestions.length]);
    
    // Use this effect to ensure layoutSuggestions are properly loaded
    useEffect(() => {
        if (layoutSuggestions && Array.isArray(layoutSuggestions) && layoutSuggestions.length > 0) {
            console.log("Layout suggestions loaded in Step4:", layoutSuggestions);
            setLocalSuggestions(layoutSuggestions);
        } else {
            console.warn("Empty or invalid layoutSuggestions received:", layoutSuggestions);
            
            // Try to load from Firestore via sessionStorage if layoutSuggestions is empty
            const loadSuggestions = async () => {
                // First try from sessionStorage for quick recovery
                try {
                    const sessionSuggestions = sessionStorage.getItem('layoutSuggestions');
                    
                    if (sessionSuggestions) {
                        const parsedSuggestions = JSON.parse(sessionSuggestions);
                        
                        if (Array.isArray(parsedSuggestions) && parsedSuggestions.length > 0) {
                            console.log("Loading layout suggestions from sessionStorage:", parsedSuggestions);
                            setLocalSuggestions(parsedSuggestions);
                            return;
                        }
                    }
                } catch (error) {
                    console.error("Error loading layout suggestions from sessionStorage:", error);
                }
                
                // If not in sessionStorage, try to load from Firestore
                try {
                    const userId = sessionStorage.getItem('aiProcessingUserId');
                    if (userId && db) {
                        const sessionRef = doc(db, 'sessions', userId);
                        const docSnap = await getDoc(sessionRef);

                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            if (data.layoutSuggestions && Array.isArray(data.layoutSuggestions) && 
                                data.layoutSuggestions.length > 0) {
                                console.log("Loading layout suggestions from Firestore:", data.layoutSuggestions);
                                setLocalSuggestions(data.layoutSuggestions);

                                // Save to sessionStorage for faster access next time
                                sessionStorage.setItem('layoutSuggestions', JSON.stringify(data.layoutSuggestions));
                                return;
                            }
                        }
                    }

                    console.log("No layout suggestions found in Firestore");
                } catch (error) {
                    console.error("Error loading layout suggestions from Firestore:", error);
                    // Show user-friendly error if it's an AI/Anthropic error
                    if (
                        error.message &&
                        (
                            error.message.includes('Anthropic') ||
                            error.message.includes('Claude') ||
                            error.message.includes('overloaded') ||
                            error.message.includes('429') ||
                            error.message.includes('timeout') ||
                            error.message.includes('rate limit') ||
                            error.message.includes('temporarily unavailable')
                        )
                    ) {
                        // You may want to set a local error state here if you have one
                        // setLocalError("Our AI service is currently busy or unavailable. Please try again after some time.");
                        alert("Our AI service is currently busy or unavailable. Please try again after some time.");
                    }
                }
                
                // If still no suggestions, check if there's a firestoreSessionData in sessionStorage
                try {
                    const sessionData = sessionStorage.getItem('firestoreSessionData');
                    
                    if (sessionData) {
                        const parsedData = JSON.parse(sessionData);
                        
                        if (parsedData.layoutSuggestions && 
                            Array.isArray(parsedData.layoutSuggestions) && 
                            parsedData.layoutSuggestions.length > 0) {
                            console.log("Loading layout suggestions from firestoreSessionData:", parsedData.layoutSuggestions);
                            setLocalSuggestions(parsedData.layoutSuggestions);
                        }
                    }
                } catch (error) {
                    console.error("Error loading layout suggestions from firestoreSessionData:", error);
                }
            };
            
            loadSuggestions();
        }
    }, [layoutSuggestions, db]);
    
    // Effect to handle canvas drawing
    useEffect(() => {
        if (showBasicPreview && canvasRef.current && quiltGrid && quiltGrid.length > 0 && gridSize) {
            console.log("Drawing basic layout preview in Step4:", {
                gridSize,
                quiltGridLength: quiltGrid.length,
                logoSrc: logoSrc ? "Available" : "Not available",
                canvasRef: canvasRef.current ? "Available" : "Not available"
            });
            
            const timer = setTimeout(() => {
                try {
                    // Parse grid dimensions to calculate appropriate canvas size
                    const [cols, rows] = gridSize.split('x').map(Number);
                    
                    // Calculate the container width to determine max canvas size
                    const container = canvasRef.current.parentElement;
                    const containerWidth = container ? container.clientWidth - 20 : 460; // 20px for padding
                    
                    // Calculate the maximum possible tile size based on container
                    const maxTileSize = Math.floor(containerWidth / cols);
                    
                    // Set canvas dimensions based on grid size and available space
                    canvasRef.current.width = cols * maxTileSize;
                    canvasRef.current.height = rows * maxTileSize;
                    
                    // Draw with size constraints to fit the container
                    drawOnCanvas(canvasRef, quiltGrid, gridSize, logoSrc, true);
                    console.log("Basic layout preview drawn successfully");
                } catch (error) {
                    console.error("Error drawing on canvas:", error);
                }
            }, 200); // Increased timeout for better reliability
            
            return () => clearTimeout(timer);
        }
    }, [showBasicPreview, quiltGrid, gridSize, logoSrc, canvasKey]);
    
    // Debug logging to verify props
    useEffect(() => {
        console.log("Step4 component received props:", {
            layoutSuggestionsCount: layoutSuggestions?.length,
            quiltGridAvailable: quiltGrid && quiltGrid.length > 0,
            gridSize,
            logoSrc: logoSrc ? "Available" : "Not available"
        });
    }, [layoutSuggestions, quiltGrid, gridSize, logoSrc]);
    
    // Toggle the basic layout preview
    const toggleBasicPreview = () => {
        setShowBasicPreview(!showBasicPreview);
        // Force canvas redraw when toggling on
        if (!showBasicPreview) {
            setCanvasKey(Date.now());
        }
    };

    // Enhanced handlers for title and description changes that preserve cursor position
    const handleLocalTitleChange = (index, e) => {
        const newValue = e.target.value;
        const cursorPosition = e.target.selectionStart;
        
        // Save cursor position for this specific input
        setTitleCursorPositions(prev => ({
            ...prev,
            [index]: cursorPosition
        }));
        
        // Call the parent handler to update the actual data
        handleTitleChange(index, newValue);
    };
    
    const handleLocalDescriptionChange = (index, e) => {
        const newValue = e.target.value;
        const cursorPosition = e.target.selectionStart;
        
        // Save cursor position for this specific textarea
        setDescriptionCursorPositions(prev => ({
            ...prev,
            [index]: cursorPosition
        }));
        
        // Call the parent handler to update the actual data
        handleDescriptionChange(index, newValue);
    };
    
    // Effects to restore cursor positions after render
    useEffect(() => {
        // Restore title input cursor positions
        Object.entries(titleCursorPositions).forEach(([index, position]) => {
            const inputRef = titleInputRefs.current[parseInt(index)];
            if (inputRef && inputRef.current) {
                inputRef.current.setSelectionRange(position, position);
            }
        });
    }, [localSuggestions, titleCursorPositions]);
    
    useEffect(() => {
        // Restore description textarea cursor positions
        Object.entries(descriptionCursorPositions).forEach(([index, position]) => {
            const textareaRef = descriptionInputRefs.current[parseInt(index)];
            if (textareaRef && textareaRef.current) {
                textareaRef.current.setSelectionRange(position, position);
            }
        });
    }, [localSuggestions, descriptionCursorPositions]);

    return (
        <div className="bg-white rounded-lg p-3 sm:p-6 shadow-md">
            <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#9CAF88]" style={{ fontFamily: "Playfair Display" }}>
                <FontAwesomeIcon icon={faMagic} className="mr-2" />
                Customize Your Layout Suggestions
            </h2>
            
            <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                {aiGeneratedLayouts && aiGeneratedLayouts.length > 0 
                    ? "Your AI-powered layouts have been generated. You can view them in the next step."
                    : localSuggestions.length > 0 
                        ? "Nostai has analyzed your quilt and created these layout suggestions. Feel free to edit the titles and descriptions before generating the final layouts."
                        : "No layout suggestions available. Please return to the previous step and generate suggestions."}
            </p>
            
            {/* Toggle button for basic layout preview */}
            <div className="mb-4 sm:mb-6">
                <button 
                    onClick={toggleBasicPreview}
                    className="flex items-center py-2 px-4 bg-[#9CAF88] hover:bg-[#8A9B79] text-white rounded-lg transition-colors text-sm sm:text-base"
                >
                    <FontAwesomeIcon icon={showBasicPreview ? faEyeSlash : faEye} className="mr-2" />
                    {showBasicPreview ? "Hide Basic Layout" : "Show Basic Layout"}
                </button>
            </div>
            
            {/* Basic layout preview */}
            {showBasicPreview && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 border rounded-lg bg-gray-50">
                    <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-center text-[#9CAF88]">
                        Your Current Basic Layout
                    </h3>
                    <div className="flex justify-center">
                        <div className="max-w-full w-full overflow-hidden p-2 bg-white border rounded-lg shadow-inner">
                            <div className="flex justify-center">
                                <canvas 
                                    ref={canvasRef}
                                    key={canvasKey}
                                    className="max-w-full h-auto"
                                    style={{ display: 'block' }} // Ensures no extra space below canvas
                                />
                            </div>
                        </div>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 text-center mt-2">
                        This is your current basic layout. AI suggestions will create alternative arrangements of these same tiles.
                    </p>
                </div>
            )}
            
            {localSuggestions.length > 0 ? (
                <div className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
                    {localSuggestions.map((layout, index) => (
                        <div key={index} className="border border-gray-300 rounded-lg p-3 sm:p-4 hover:border-blue-300 transition-colors">
                            <div className="mb-3 flex items-center">
                                <div className="bg-[#9CAF88] text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                                    {index + 1}
                                </div>
                                <div className="relative flex-1">
                                    <input
                                        ref={titleInputRefs.current[index]}
                                        value={layout.title || ''}
                                        onChange={(e) => handleLocalTitleChange(index, e)}
                                        className="w-full text-base sm:text-lg font-semibold border-b border-gray-300 focus:border-blue-500 focus:outline-none py-1"
                                        style={{ fontFamily: "Playfair Display" }}
                                        placeholder="Layout title"
                                        disabled={aiGeneratedLayouts && aiGeneratedLayouts.length > 0}
                                    />
                                    <FontAwesomeIcon icon={faPencilAlt} className="text-gray-400 absolute right-2 top-3" />
                                </div>
                            </div>
                            
                            <div className="relative">
                                <textarea
                                    ref={descriptionInputRefs.current[index]}
                                    value={layout.description || ''}
                                    onChange={(e) => handleLocalDescriptionChange(index, e)}
                                    className="w-full border rounded-md p-2 sm:p-3 focus:border-blue-500 focus:outline-none text-sm sm:text-base"
                                    style={{ 
                                        fontFamily: "Playfair Display", 
                                        height: "50px",  // Set explicit height instead of min-height
                                        resize: "none"   // Disable textarea resizing
                                    }}
                                    placeholder="Layout description"
                                    disabled={aiGeneratedLayouts && aiGeneratedLayouts.length > 0}
                                />
                                <FontAwesomeIcon icon={faPencilAlt} className="text-gray-400 absolute right-3 top-3" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4 mb-4 sm:mb-6">
                    <p className="text-yellow-700 text-sm sm:text-base">No layout suggestions available. Please go back and generate suggestions first.</p>
                </div>
            )}
            
            {/* Navigation buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 mt-4 sm:mt-6">
                <button
                    onClick={prevStep}
                    className="order-2 sm:order-1 w-full sm:w-auto px-4 py-2 bg-[#B8A99A] hover:bg-[#A69688] text-white rounded-lg flex items-center justify-center sm:justify-start"
                >
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back to Basic Layout
                </button>
                
                {aiGeneratedLayouts && aiGeneratedLayouts.length > 0 ? (
                    <button
                        onClick={nextStep}
                        className="order-1 sm:order-2 w-full sm:w-auto px-4 py-2 bg-[#9CAF88] hover:bg-[#8A9B79] text-white rounded-lg flex items-center justify-center sm:justify-end"
                    >
                        View AI Layouts <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </button>
                ) : localSuggestions.length > 0 ? (
                    <button
                        onClick={handleGenerateLayouts}
                        disabled={loadingAI}
                        className={`order-1 sm:order-2 w-full sm:w-auto px-4 py-2 ${
                            loadingAI ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#9CAF88] hover:bg-[#8A9B79]'
                        } text-white rounded-lg flex items-center justify-center sm:justify-end`}
                    >
                        {loadingAI ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                                <span className="text-sm sm:text-base">Generating Layouts...</span>
                            </>
                        ) : (
                            <>
                                <span className="text-sm sm:text-base">Generate AI Layouts</span> <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </>
                        )}
                    </button>
                ) : null}
            </div>
            
            {aiError && (
                <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-3 sm:p-4">
                    <p className="text-red-700 text-sm sm:text-base">{aiError}</p>
                </div>
            )}
        </div>
    );
};

export default Step4;
